import React, { useEffect, useState } from 'react';
import { X, ExternalLink, ShieldAlert } from 'lucide-react';

/**
 * FloatingPortal — verified "observation window" iframe overlay.
 *
 * Slides in from the right at 50% viewport width, full height, with an
 * amber-edged header that reads as a Federation observation deck and a
 * semi-transparent backdrop on the left (click to dismiss).
 *
 * Note: Many corporate AI portals (ChatGPT custom GPTs included) send
 * X-Frame-Options: deny or a CSP frame-ancestors directive that blocks
 * iframe embedding. When that happens the iframe renders blank — we
 * surface a Truth-Gate-compliant fallback with a direct "Open in new
 * tab" link so the user is never left staring at an empty pane.
 */
export default function FloatingPortal({ portal, isOpen, onClose }) {
  const [embedBlocked, setEmbedBlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return undefined;
    setEmbedBlocked(false);
    setLoading(true);
    // Lock body scroll while overlay is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, portal?.id]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Heuristic: if the iframe hasn't dispatched its `load` event after
  // 6s, treat the embed as blocked. Most browsers DO fire `load` on a
  // CSP-blocked frame too — but the inner document is opaque, so we
  // pair this timer with a visible "Open in new tab" affordance.
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => {
      if (loading) setEmbedBlocked(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [isOpen, loading]);

  if (!isOpen || !portal) return null;

  const isOldMind = portal.category === 'old-mind';
  const accent = isOldMind ? '#fbbf24' : '#22d3ee'; // amber for old-mind, cyan for federation

  return (
    <div
      data-testid="floating-portal-root"
      data-portal-id={portal.id}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        display: 'flex',
        justifyContent: 'flex-end',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Backdrop — click to dismiss */}
      <button
        type="button"
        aria-label="Close portal"
        data-testid="floating-portal-backdrop"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(2px)',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      />

      {/* Sliding panel */}
      <aside
        data-testid="floating-portal-panel"
        style={{
          position: 'relative',
          width: '50vw',
          minWidth: '380px',
          maxWidth: '100vw',
          height: '100vh',
          background: '#050505',
          borderLeft: `1px solid ${accent}55`,
          boxShadow: `-20px 0 60px rgba(0,0,0,0.6), inset 1px 0 0 ${accent}22`,
          display: 'flex',
          flexDirection: 'column',
          animation: 'verified-portal-slide-in 280ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          color: '#e5e7eb',
        }}
      >
        <style>{`
          @keyframes verified-portal-slide-in {
            from { transform: translateX(100%); opacity: 0.6; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* Header — amber observation-deck strip */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 18px',
            borderBottom: `1px solid ${accent}33`,
            background: `linear-gradient(180deg, ${accent}1a 0%, rgba(0,0,0,0) 100%)`,
            boxShadow: `0 0 24px ${accent}10`,
          }}
        >
          <span
            data-testid="floating-portal-category-badge"
            style={{
              fontSize: '10px',
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontWeight: 700,
              letterSpacing: '0.18em',
              padding: '4px 8px',
              borderRadius: '4px',
              border: `1px solid ${accent}66`,
              color: accent,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {isOldMind ? 'Old Mind · Observation' : 'Federation · Portal'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              data-testid="floating-portal-title"
              style={{ fontSize: '14px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}
            >
              {portal.name}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.55)',
                fontFamily: 'ui-monospace, Menlo, monospace',
                marginTop: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={portal.url}
            >
              {portal.url.replace(/^https?:\/\//, '')}
            </div>
          </div>
          <a
            href={portal.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="floating-portal-open-new-tab"
            title="Open in a new tab"
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              border: `1px solid ${accent}55`,
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              transition: 'background 120ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}22`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            type="button"
            onClick={onClose}
            data-testid="floating-portal-close"
            aria-label="Close portal"
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.15)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Iframe body */}
        <div style={{ flex: 1, position: 'relative', background: '#000' }}>
          <iframe
            key={portal.id}
            data-testid="floating-portal-iframe"
            src={portal.url}
            title={portal.name}
            onLoad={() => setLoading(false)}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer-when-downgrade"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: '#000',
            }}
          />
          {loading && !embedBlocked && (
            <div
              data-testid="floating-portal-loading"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '12px',
                fontFamily: 'ui-monospace, Menlo, monospace',
                letterSpacing: '0.1em',
              }}
            >
              Opening observation window…
            </div>
          )}
          {embedBlocked && (
            <div
              data-testid="floating-portal-blocked"
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, #0a0a0a 0%, #050505 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                textAlign: 'center',
                gap: '14px',
              }}
            >
              <ShieldAlert className="w-10 h-10" style={{ color: accent }} />
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>
                This portal blocks embedding.
              </div>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', maxWidth: '380px', lineHeight: 1.5 }}>
                The Old Mind sends an <code style={{ color: accent }}>X-Frame-Options</code> header
                that refuses to load inside a verified observation window. The Truth Gate
                requires we tell you: we can&apos;t show it here, but you can open it directly.
              </p>
              <a
                href={portal.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="floating-portal-blocked-open"
                style={{
                  marginTop: '8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: accent,
                  color: '#000',
                  borderRadius: '999px',
                  fontWeight: 700,
                  fontSize: '13px',
                  textDecoration: 'none',
                }}
              >
                <ExternalLink className="w-4 h-4" />
                Open {portal.name} in a new tab
              </a>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
